import { FirebaseOptions } from 'firebase/app';
import {
    AccountDeleteConfirmationType,
    CardActionsFeature,
    EmptyCartOptions,
    MenuItem,
    ThemeFeature
} from './lib/enums/features.enum';
import {
    DisruptionFilters,
    DisruptionMessageType,
    TravelMedia,
    HeaderElement,
    ExternalLinksConfig,
    DropdownMenuItem,
    DropdownMenuData,
    NotificationPreferenceChannel,
    NotificationTabType
} from './lib/ridango.models';
import { PlatformConfig } from '@ionic/angular';
import { ProductCategoryAreaNameEnum } from './lib/enums/product-category-area-name.enum';

export default interface Env {
    /** Options for account details page */
    ACCOUNT?: AccountSettings;
    /** How user confirms the blocking of the card */
    ACCOUNT_DELETE_CONFIRM_TYPE?: AccountDeleteConfirmationType;
    ALLOWED_PATHS?: string[];
    /** Settings for having a specific analytics vendor */
    ANALYTICS: {
        /** Vendor type provider */
        TYPE: 'google-gtm' | 'matomo';
        /** Main ID from the vendor, if there is only one */
        ID?: string;
        /** URL of the vendor for a specific analytics server */
        URL?: string;
        /** If the client has different channels for different platforms, this overrides the default main ID with different IDs */
        CHANNELS?: {
            WEB?: string;
            ANDROID?: string;
            IOS?: string;
        };
    };

    TICKETS_CHANNEL?: string;

    DISRUPTION_SUBSCRIPTIONS?: {
        SELECTION_LIMIT?: number;
        SEARCH_INITIAL_LIMIT?: number;
        SEARCH_LOAD_MORE_LIMIT?: number;
        ENABLED_TABS?: NotificationTabType[];
    };

    TICKET?: {
        DEFAULT_PERIOD_TICKET_DURATION_DAYS?: number;
        /** Maximum validity period for period tickets in days (including start day) */
        PERIOD_TICKET_MAX_VALIDITY_DAYS?: number;
        /** Maximum validity period for custom-range period tickets in days (including start day) */
        PERIOD_TICKET_MAX_VALIDITY_DAYS_CUSTOM?: number;
        /** If false, product icon will be hidden in TICKETS LIST view, otherwise it will be shown */
        SHOW_PRODUCT_ICON?: boolean;
        /** Defines the color scheme for the buy ticket button in ticket list view */
        BUY_TICKET_BUTTON_COLOR_SCHEME?: 'primary' | 'secondary';
        /** If true, ACTIVE and INACTIVE titles will be shown in uppercase */
        UPPERCASE_TITLE?: boolean;
        /** If true, vertical separator will be shown before ticket actions */
        SHOW_VERTICAL_SEPARATOR_BEFORE_TICKET_ACTIONS?: boolean;
        /** If true, QR code will be hidden for inactive tickets. Used when Ridango devices aren't used to activate ticket automatically */
        HIDE_QR_FOR_INACTIVE_TICKETS?: boolean;

        /** If true, starting and ending dates will be shown for pending tickets */
        SHOW_PENDING_ACTIVATION_DATES?: boolean;
        /** If true, expire dates will be shown for expired tickets in detail view */
        SHOW_HISTORY_TICKET_EXPIRED_DATES?: boolean;
        /** If true tickets will be checked for same validity activated tickets before manual activation */
        MANUAL_ACTIVATION_CHECK_VALIDITY: boolean;
        /** If true, ticket details page will show icon for "Show/Hide details" */
        SHOW_TICKET_DETAILS_MORE_INFO_TOGGLE_ICON?: boolean;
    };
    /** Time to refresh departure time, it should be small, since it will affect performance.
     *  We already update times locally, but this env needed to fetch remote data sometimes
     * */
    SECONDS_TO_REFRESH_DEPARTURE_TIMES: number;
    /** Consumer can define private routes for application, user should be logged in app for this routes */
    PRIVATE_PATHS?: string[];
    /** URL for old version of API */
    API_BASE_OLD_URL?: string;
    /** URL for API */
    API_BASE_URL?: string;
    DEEP_LINKING?: boolean;
    SSO_BETWEEN_APP?: boolean;
    API_GATEWAY_URL?: string;
    /** Used to toggle and style UI elements and as localStorage variable names prefix,
     * e.g. `vl-web-cookies-accepted`, `vl-web-access-user` */
    APP_NAME?: string;
    /** Local path for images, icons and fonts used by the project */
    ASSETS_DIR?: string;
    ALLOWED_FEATURE_FLAGS: string[];
    /** Enables specific ways to auth a user; if no value/empty array then no auth features will be added */
    AUTH_FEATURES?: ('ID-CARD' | 'MOBILE-ID' | 'SMART-ID' | 'VIISP' | 'FREJA')[];
    BANKCARD_HISTORY_DATE_FORMAT?: string;
    TICKET_CARD_HIDE_PRICE?: boolean;
    TICKET_REDIRECT_TO_DETAILS_FROM_HISTORY?: boolean;
    /** App's URL, basis of links */
    BASE_URL?: string;
    OIDC_REDIRECT_URL?: string;
    HIDE_MENU_OPEN_KEYBOARD?: boolean;
    BOB?: BobSettings;
    BUS_LOCATION_CLEARING_INTERVAL_IN_SECONDS?: number;
    BUS_LOCATION_EXPIRING_IN_SECONDS?: number;
    BUS_STOPS_PREVIOUS_SEARCHES_LIMIT?: number;
    CEMV_HOST_APP?: string;
    /** Defines channel that current application is */
    /** This can be single or coma seperated several channels "web,validator" */
    CHANNEL?: 'web' | 'web_corp' | 'mobile' | 'validator' | 'user' | 'seller' | 'device' | 'recurring_payment' | string;
    HISTORY_CHANNEL?:
        | 'web'
        | 'web_corp'
        | 'mobile'
        | 'validator'
        | 'user'
        | 'seller'
        | 'device'
        | 'recurring_payment'
        | string;
    /** URL for core API */
    CORE_API_URL?: string;
    /** Disable bounce effect on ios */
    DISABLE_BOUNCE_IOS_EFFECT?: boolean;
    /** Remove uppercase from all screen */
    REMOVE_UPPERCASE?: boolean;
    STATUS_BAR?: {
        HIDE_LOGIN_FLOW_BAR?: boolean;
        WHITE_BAR_ICON_LIGHT?: string[];
        COLOR_LOGIN_FLOW?: string;
        COLOR_ONBOARD_FLOW?: string;
        SPECIFIC_PATHS?: { [key: string]: string };
    };
    COUNTRY_CODE?: string;
    /** Landing page county slider slides (logos are added manually into project) */
    COUNTY_SLIDER?: {
        link: string;
        logo?: string;
        name: string;
    }[];
    CREATE_ORDER_SHOW_ADDITIONAL?: boolean;
    /** Used in elron and sormland
     * @deprecated
     */
    DATE_PICKER_DISPLAY_FORMAT?: string;
    DEFAULT_LATITUDE?: number;
    /** @deprecated Used only in Elron. Use locale.json instead */
    DEFAULT_LOCALE?: string;
    DEFAULT_LONGITUDE?: number;
    ELRON_HOME_PAGE?: string;
    /** NEW CHANGE PASSWORD PAGE */
    CHANGE_PASSWORD_NEW?: boolean;
    EMPTY_CART_OPTIONS?: EmptyCartOptionsSettings;
    /** Environment in which the app is running */
    ENV?: string;
    FILTER_PRODUCT_CATEGORY_BY_AREA?: boolean;
    FIREBASE?: FirebaseOptions;
    APP_NOTIFICATIONS?: AppNotifications;
    /** Configuration for which notification preferences to show per channel */
    NOTIFICATION_PREFERENCES?: NotificationPreferenceConfig;
    FOOTER?: FooterSettings;
    /** Opt-ins and parameters for [Native Intl API formatting](https://ridango.atlassian.net/wiki/spaces/WE/pages/4131225633/Native+Intl+API) */
    FORMATTING?: FormattingSettings;
    HEADER?: HeaderSettings;
    HIDE_LANGUAGE_SELECTOR?: boolean;
    COMPANY_INVOICE_PAYMENT?: boolean;
    HOME?: HomeSettings;
    /** Corporate customer web (äriklient) */
    IS_CORPORATE?: boolean;
    IS_LIVE?: boolean;
    /** Toggles capacitor app specific components/rules */
    IS_MOBILE_APP?: boolean;
    JOURNEY_PLANNER?: JourneyPlannerSettings;
    /** Landing page actions with travel cards (icons are added manually into project) */
    LANDING_TRAVELCARD_LINKS?: {
        class?: string;
        icon: string;
        link: string;
        tag: string;
    }[];
    LAUNCHDARKLY_CLIENT_ID?: string;
    /**
     * Used in moneyFormat pipe
     * @deprecated
     * */
    LOCALE?: string;
    /** Used to show mobile menu with new design */
    SHOW_NEW_MOBILE_MENU?: boolean;
    /** Allow mobile system OS text scaling for app */
    ALLOW_DYNAMIC_CONTENT_SCALING?: boolean;
    /** Show Aztec code for loaned ticket */
    TICKET_SHOW_AZTEC_LOANED?: boolean;
    MAIN_MENU_ITEMS?: {
        external: boolean;
        tag: string;
        to: string;
    }[];
    MAP?: MapSettings;
    /* You can set default zoom value for map */
    INITIAL_ZOOM_MAP?: number;
    MAX_DRT_PASSENGERS?: number;
    MIN_DECIMAL_DIGITS?: number;
    ARLANDA_STOP_ID: string;
    NO_THEMING?: boolean;
    /** Landing page notices slide count; generates `NOTICE_SLIDE_0`, `NOTICE_SLIDE_1`, etc. PO tags */
    NOTICE_SLIDES?: number;
    NOTIFICATION_PAGE_USER?: boolean;
    JOINT_TICKET_MAX_PERIOD?: number;
    NOTIFICATION_CHANNELS?: string[];
    /** Web client for authentication server */
    OAUTH_CLIENT_ID?: string;
    /** URL to authentication server */
    OAUTH_URL?: string;
    ONBOARDING?: OnboardingSettings;
    ONDEMAND?: OndemandSettings;
    OTP_BASE_URL?: string;
    OTP_INDEX?: number | string;
    /** Enable to refresh ticket page by swipe up */
    REFRESH_TICKET_PAGE?: boolean;
    PAYMENT_GATEWAY_URL?: string;
    PAYMENT_PORTAL_URL?: string;
    /** Controls whether Payment Portal is displayed in payment methods list (defaults to true / shown) */
    SHOW_PAYMENT_PORTAL?: boolean;
    PAYMENT_METHOD_USE_NAME?: boolean;
    /** Component of the app to return to after payment (concatenated with payment return URL) */
    PAYMENT_RETURN_COMPONENT?: string;
    /** Component of the app to return to after user aborted the payment (concatenated with payment return URL) */
    PAYMENT_ABORT_COMPONENT?: string;
    /** URL of the site to return to after payment (concatenated with payment component) */
    PAYMENT_RETURN_URL?: string;
    /** Date format to be displayed on period tickets */
    PERIOD_TICKET_DATE_FORMAT?: string;
    /** Expired date format for inactive tickets */
    TICKET_EXPIRES_DATE_FORMAT?: string;
    /** Used in Elron for price list fetching */
    PERIOD_TICKET_EXCLUDED_TYPE?: string;
    /** Used in Elron for price list fetching */
    PERIOD_TICKET_IDENTIFIER?: string;
    /** Used for apps that have separate Leaflet access tokens to measure amount of calls made */
    LEAFLET_ACCESS_TOKEN?: string;
    /** Regex to compare postal code input against */
    POSTAL_CODE_PATTERN?: string;
    /**
     * Date format for date to be formatted by Angular
     * [DatePipe](https://angular.io/api/common/DatePipe)
     * @deprecated
     */
    PRIMARY_DATE_FORMAT?: string;
    /**
     * Date format for date to be formatted by
     * [Moment.js](https://momentjs.com/docs/#/displaying/)
     */
    PRIMARY_DATE_FORMAT_MOMENT?: string;
    /**
     * Date format for date to be formatted by Angular
     * [DatePipe](https://angular.io/api/common/DatePipe)
     */
    PRIMARY_DATE_FORMAT_NGPIPE?: string;
    /**
     * date format for purchase flow - step activation at time.
     */
    ACTIVATION_DATE_DISPLAY_FORMAT?: string;
    /**
     * Date format for date and time to be formatted
     */
    PRIMARY_DATE_TIME_FORMAT?: string;
    /**
     * Default Time format
     */
    PRIMARY_TIME_FORMAT?: string;
    /** Enables production mode (Live) in Angular app */
    production?: boolean;
    /** Options for purchase flow */
    PURCHASE?: PurchaseSettings;
    /** Purchase history table column list */
    PURCHASE_HISTORY_TABLE_COLUMNS?: (
        | 'date'
        | 'purchaseType'
        | 'purchaseName'
        | 'purchasePlace'
        | 'price'
        | 'total_sum'
        | 'receipt'
    )[];
    /** Purchase history table column list */
    PURCHASE_HISTORY_TICKETS_TABLE_COLUMNS?: ('name' | 'price' | 'starting' | 'ending' | 'status' | 'receipt')[];
    /** Used to override env.REGION_ID for RT vehicle status */
    REALTIME_REGION_ID?: number;
    /** Websocket path for vehicle status */
    REALTIME_VEHICLE_STATUS_PATH?: string;
    /** Websocket base URL */
    REALTIME_WEBSOCKET_URL?: string;
    /** Threshold to format realtime until departure */
    REALTIME_THRESHOLD_MINUTES?: number;
    /** Needed to make requests to the API */
    REGION_ID?: number;
    /** Default currency for the region */
    CURRENCY?: string;
    /** Options for account registration */
    REGISTRATION?: RegistrationOptions;
    /** Options for profile update */
    PROFILE_UPDATE?: ProfileUpdateOptions;
    /** Determines if regional-specific logic is applied in the booking process */
    REGIONAL_BOOKING?: boolean;
    /** Options for related card */
    RELATED_CARD?: RelatedCardSettings;
    SEARCH_REGION?: string;
    SHOW_PWA?: boolean;
    SIGN_ON_PAGE?: boolean;
    /** URL to CDN server */
    STATIC_CONTENT_BASE_URL?: string;
    STRONG_AUTH_ENABLED?: boolean;
    /** Transfer anonymous data after login  */
    TRANSFER_ANONYMOUS_DATA?: boolean;
    THEME_COLOR?: string;
    /** Theme name used by app */
    THEME_NAME?: ThemeFeature;
    TICKET_ACTIONS?: (
        | 'RETURN_TICKET'
        | 'SEND_TICKET'
        | 'BUY_TICKET_AGAIN'
        | 'ADD_TO_FAVORITE_TICKET'
        | 'LEND_TICKET'
        | 'AWAITING_REACTIVATION'
        | 'REMOVE_HISTORY_TICKET'
        | 'RETURN_LEND_TICKET'
    )[];
    SHOW_SELECTOR_TIME_LEND_TICKET?: boolean;
    /** SEND_TICKET identifier variations  */
    SEND_TICKET_IDENTIFIER?: 'email' | 'emailOrPhone';
    /** Favorite tickets enabled (purchase v2) */
    SHOW_FAVORITE_TICKETS?: boolean;
    /** Used in tickets list page card to display history tab */
    SHOW_TICKET_HISTORY?: boolean;
    SHOW_TICKET_HISTORY_STATUS?: boolean;
    SHOW_TICKET_HISTORY_CANCELLED?: boolean;
    SHOW_TICKET_HISTORY_NOTE?: boolean;
    /** Used in tickets list page card to display validity */
    TICKET_CARD_DATE_FORMAT?: string;
    /** Whether to show zone map on ticket elements */
    TICKET_HAS_ZONE_MODAL?: boolean;
    /** Enables manual ticket activation by a user */
    TICKET_MANUAL_ACTIVATION_ALLOWED?: boolean;
    /** Used in tickets list page card to display qr chip */
    TICKET_CARD_HIDE_QR_CHIP?: boolean;
    /** Used in ticket detail page to display ticket details */
    TICKET_DETAILED_INFORMATION?: boolean;
    /**
     * Ticket validity progress indicator settings.
     * - SHOW_IN_TICKET_CARD / SHOW_IN_TICKET_DETAILS: enable indicator per view (enabled unless explicitly false)
     * - TICKET_CARD_INDICATOR_TYPE: visual type for ticket card (spinner|bar)
     * - TICKET_CARD_TIME_LABEL_PLACEMENT / TICKET_DETAILS_TIME_LABEL_PLACEMENT: where to render remaining time text (top|right|hidden)
     * - TICKET_CARD_USE_H_MIN_FORMAT / TICKET_DETAILS_USE_H_MIN_FORMAT: show remaining time as "11h 22min" instead of "11:22"
     * - TICKET_CARD_HIDE_PRICE_FOR_ACTIVE: hide card price for active tickets while keeping inactive ticket prices visible
     */
    TICKET_PROGRESS_INDICATOR?: {
        SHOW_IN_TICKET_CARD?: boolean;
        SHOW_IN_TICKET_DETAILS?: boolean;
        TICKET_CARD_INDICATOR_TYPE?: 'spinner' | 'bar';
        TICKET_CARD_TIME_LABEL_PLACEMENT?: 'top' | 'right' | 'hidden';
        TICKET_DETAILS_TIME_LABEL_PLACEMENT?: 'top' | 'right' | 'hidden';
        TICKET_CARD_USE_H_MIN_FORMAT?: boolean;
        TICKET_DETAILS_USE_H_MIN_FORMAT?: boolean;
        TICKET_CARD_HIDE_PRICE_FOR_ACTIVE?: boolean;
    };
    /** Used to show device ticket for logged-in user ( by default = true ) */
    SHOW_DEVICE_TICKET_ACCOUNT?: boolean;
    TICKET_SORT_ORDER?: 'asc' | 'desc';
    /** @deprecated */
    TIMEZONE?: string;
    TPS_BANKCARD_IFRAME_URL?: string;
    /** SFEY ecomm url v2 update */
    USE_ECOMM_V2?: boolean;
    /** Bankcard iFrame URL style */
    ECOMM_FORM_SETTINGS?: {
        DEFAULT_STYLE_NAME?: string;
        HIGH_CONTRAST_STYLE_NAME?: string;
    };

    /** POEditor project ID for translations */
    TRANSLATIONS_ID?: number | string;
    /**
     * Often the day for public transport operations is ending sometime after midnight (e.g. 28.06. 04:00),
     * but as the period ticket wouldn’t be valid for the whole day,
     * we have to show previous day as the last day (e.g. 27.06.).
     * @deprecated Use `AppStartupService.getTransportationDayOffset` instead
     */
    TRANSPORT_DAY_END_PREV_DAY?: boolean;
    /**
     * @deprecated Prefer `FORMATTING.USE_TRANSPORTATION_DAY_OFFSET` or `PURCHASE.USE_TRANSPORTATION_DAY_OFFSET`.
     * Still read as fallback in `AppStartupService` and related services.
     */
    USE_TRANSPORTATION_DAY_OFFSET?: boolean;
    /**
     * @deprecated Prefer `FORMATTING.SHORT_PERIOD_PRODUCT_TYPES_SKIP_OFFSET`.
     * Still read as fallback in `DateFormattingService`.
     */
    SHORT_PERIOD_PRODUCT_TYPES_SKIP_OFFSET?: string[];
    USE_TAILWIND?: boolean;
    /** Ticket history table column list */
    VALIDATION_TABLE_COLUMNS?: ('date' | 'time' | 'medium' | 'ticketName' | 'lineName' | 'lineStop' | 'lineZone')[];
    /** Show new joint tickets in Elron. This flag system can be removed after Ühispilet has been deployed to live.  */
    SHOW_NEW_JOINT_TICKETS?: boolean;
    WMB?: {
        TENANT_ID: number;
        TENANT_NAME?: string;
        WMB_PUBLIC_API: string;
        WMB_PRIVATE_API?: string;
        CURRENCY?: string;
        DONT_USE_FOR?: string[];
        DISRUPTION_MESSAGES?: boolean;
        /** Translations are fetched from WMB (older clients are still getting translations from api-router) */
        TRANSLATION_PROVIDER?: boolean;
        /** Stoptimes endpoint returns also route data  */
        STOPTIMES_WITH_ROUTE_DATA?: boolean;
        /** Use WMB provided authentication endpoints */
        AUTH?: {
            CLIENT_ID: string;
        };
    };
    /** If data source is NEW AFC or OLD AFC  */
    IS_NEW_AFC: boolean;
    AFC_TOKEN_ENDPOINTS: string[];
    PRODUCT_CATEGORY_AREA_NAME?: ProductCategoryAreaNameEnum;
    AREA_MAP?: {
        PRODUCT_AREA_DISPLAY_OPTION?: PRODUCT_AREA_DISPLAY_OPTION;
        OREBRO_STATIC_BASE_PATH?: string;
        OREBRO_DEFAULT_IMAGE_SRC?: string;
        SVG_IMAGE_SRC?: string;
    };
    SHOW_DISRUPTION_LIST_NOTIFICATION_BUTTONS?: boolean;
    USE_RETAILERS_WITH_MENU?: boolean;
    ENABLE_CHOOSE_STARTING_DATE?: boolean;
    SHOW_SEPARATOR_BETWEEN_MONTH_AND_TIME_ON_CALENDAR?: boolean;
    SHOW_FEEDBACK_MODAL?: boolean;
    SHOW_BANNERS?: boolean;
    EXPLORE: {
        STOP_VIEW: StopOptions;
        /** Default is true. If set to false will not filter departures by date in stop-details  */
        INCLUDE_ONLY_ONE_DATE: boolean;
        SHOW_FAVORITE_STOPS: boolean;
        SHOW_REFRESH_BUTTON?: boolean;
        LOAD_MORE_FAVORITE_STOPS_BY?: number;
        SHOW_CANCELLED_STATUS: boolean;
        SHOW_MUNICIPALITY_NAME?: boolean;
        SHOW_MUNICIPALITY_IN_STOP_NAME?: boolean;
        SHOW_FAVORITE_LOCATIONS: boolean;
        ORIGINAL_ROUTE_NAME?: boolean;
        ROUTE_STATIC_COLOR?: string;
        USE_TRIP_NAME_FOR_DIRECTION_OF_BUS?: boolean;
        SHOW_REALTIME_ICON?: boolean;
        SHOW_GROUPED_STOP_ROUTES?: boolean;
        HIDE_FAVORITE_FROM_MAP?: boolean;
        HIDE_SHORT_NAME?: boolean;
        SELECTED_BUS_STOPPED?: boolean;
        SHOW_FAVORITE_SCHEDULE: boolean;
        HIDE_FAVORITE_SCHEDULE_PRINT: boolean;
        HIDE_FAVORITE_SCHEDULE_ADDITIONAL_INFO: boolean;
        MAX_DEPARTURES: number;
        NUMBER_OF_DEPARTURES_IN_LIST: number;
        MAX_DEPARTURES_WITH_SELECTED_ROUTE_FILTER: number;
        SHOW_SELECTED_ROUTE_TRANSPORT: boolean;
        ZOOM_LEVEL_LOCATION: number;
        ROUTE_DETAIL_STATUS_ENABLED: boolean;
        CLEAR_ROUTE_LINE_AFTER_LOCATION_SELECTION: boolean;
        ROUTE_DETAIL_LATE_THRESHOLD: number;
        ROUTE_DETAIL_EARLY_THRESHOLD: number;
        IMAGE_MAP_LOCATION_PATH?: string;
        ALERTS_ON_STOPS?: boolean;
        ALERTS_ON_ROUTES?: boolean;
        ALERTS_BUBBLE_CLICK?: boolean | 'arrow-only';
        ALERTS_LIMIT_TITLE?: number;
        ALERTS_SHOW_MORE?: boolean;
        SORT_DEPARTURE_LIST?: boolean;
        DEPARTURES_REQUEST_MAX_NO_PATCHING: boolean;
        SHOW_ROUTES?: boolean;
        SHOW_ROUTE_GROUPS: boolean;
        SHOW_ROUTE_GROUP_AGENCY: boolean;
        MAP_SELECTED_STOP_ICON_SIZE?: number;
        PATTERN_DESC_SKIP_FORMATTING?: boolean;
        PATTERN_LABEL_PREFER_HEADSIGN?: boolean;
        SCHEDULE_TIME_THRESHOLD_EARLY?: number;
        SCHEDULE_TIME_THRESHOLD_LATE?: number;
        HIDE_DOUBLE_TIMES?: boolean;
        ICON_COLOR: string;
        ENABLE_ROUTES_SORTING?: boolean;
        USE_OTP_ROUTES_IN_COMPONENT?: boolean;
        HIDE_SAVED_LOCATIONS_TOGGLE?: boolean;
        BREAKPOINT_INDEX?: number;
        BUS_AUTO_CENTERING?: boolean;
        USE_CONTRAST_ICON?: boolean;
        SHOW_ICON_FOR_NEARBY_MORE_STOPS?: boolean;
        USE_TRASH_BIN_FOR_REMOVE_FAVORITE_STOP?: boolean;
        /** Setting this to true means that buses toggle won't be hidden in explore when zoomed out  */
        ALWAYS_SHOW_BUSES_TOGGLE?: boolean;
    };
    RECAPTCHA: {
        ALLOWED: boolean;
        SITEKEY: string;
    };
    TIMEPOINTS_ENABLED?: boolean;
    APPROXIMATE_TIME_THRESHOLD_MINUTES?: number;
    FIXED_TIME_THRESHOLD_MINUTES?: number;
    ENABLE_BADGE_MESSAGES?: boolean;
    EXPIRED_QR_TICKETS_AVAILABLE?: string;
    SEARCH_SETTINGS?: {
        MINIMUM_SEARCH_CHARACTERS: number;
        HIDE_CODE?: boolean;
        SHOW_DESCRIPTION: boolean;
        SHOW_LOAD_MORE: boolean;
        SEARCH_PARAMETERS: ('STOP_ROUTES' | 'ADDRESSES' | 'POI' | 'ADDRESSES_AND_POI')[];
    };
    SPLIT_STOP_ROUTES_SEARCH_RESULTS?: boolean;
    DISRUPTION_MESSAGES?: DisruptionMessagesSettings;
    ENABLE_PURCHASE_V2?: boolean;
    ENABLE_WALLET_V2?: boolean;
    ENABLE_TERMS_V2?: boolean;
    ENABLE_TERMS_WMB_VERSION?: boolean;
    BUS_ICON_TYPE?: BusIconTypeEnum;
    PURCHASE_HISTORY_BACK_BUTTON?: boolean;
    PURCHASE_FLOW?: PurchaseFlowSettings;
    WALLET_V2?: WalletV2Settings;
    /** Link to redirect the user on anchor text in the cookie consent text */
    COOKIE_CONSENT_LINK?: string;
    /** Customization for mobile menu */
    MOBILE_MENU_CUSTOMIZATION?: MobileMenuCustomization;
    /** Config for anonymous user functionality */
    DEVICE_CUSTOMER_ENABLED?: boolean;
    /** Pages which are available for anonymous user */
    ALLOWED_PATHS_FOR_DEVICE_CUSTOMER?: string[];
    /** Endpoints which should be duplicated(normal user and anonymous) if anonymous user functionality enabled  */
    DEVICE_AND_USER_DUPLICATE_ENDPOINTS?: string[];
    /** Endpoints which can be used by anonymous user if there isn't usual user */
    DEVICE_AND_USER_PRIORITY_ENDPOINTS?: string[];
    /** Send customer language to backend */
    SEND_CUSTOMER_LANGUAGE_TO_BACKEND?: boolean;
    /** Set default country code like "+372" */
    DEFAULT_PHONE_COUNTRY_CODE?: string;
    /** Set default redirect url after login example: ['account', 'cards'] */
    DEFAULT_REDIRECT_AFTER_LOGIN?: string[];
    /** Set default redirect url after account deletion example: ['login'] */
    DEFAULT_REDIRECT_AFTER_ACCOUNT_DELETE?: string[];
    /** Set default redirect url after register redirect example: ['account', 'cards'] */
    DEFAULT_REDIRECT_AFTER_REGISTER?: string[];
    /** Filter purchases (/ticket/active, validation/history) by channel */
    FILTER_PURCHASES_BY_CHANNEL?: boolean;
    /** Filter purchases (/ticket/active, validation/history) by `${channel},seller` */
    FILTER_PURCHASES_BY_CHANNEL_SELLER?: boolean;
    /** Allow the user to pick any period in purchase history */
    PURCHASE_CALENDAR_FREE_SELECT?: boolean;
    /** Hide QR code for tickets */
    HISTORY_TICKET_HIDE_QR_CODE?: boolean;
    HISTORY_TICKET_SHOW_STATUS?: boolean;
    HIDE_HISTORY_CODE?: boolean;
    ZOOMABLE_QR_CODE?: boolean;
    HIDE_APP_FOOTER_FOR_ROUTES?: string[];
    TICKET_LIST_V2_ENABLED?: boolean;
    USE_NEW_WEBVIEW?: boolean;
    /** Change amount validators in component WalletProductSelectorComponent */
    AMOUNT_VALIDATORS?: AmountValidators;
    OFFLINE_MODE?: OfflineModeSettings;
    /** Disable field Id document if user has personalization */
    PERSONALIZATION_WITHOUT_ID_DOC?: boolean;
    /** Additional check if receiver email is customer - triggers popup warning if receiver is not customer */
    CHECK_TICKET_EMAIL_RECEIVER_IS_CUSTOMER?: boolean;
    BOOKLET_TICKETS_WITH_TRIPS_REMAINING_TEXT?: boolean;
    TICKET_DETAILS_VALIDITY_RANGE_ONLY?: boolean;
    /**
     * Object of routes where key is an app route (without locale prefix) and value is an array of `[platform, url]`
     * pairs. Platform is [defined by Ionic](https://ionicframework.com/docs/angular/platform#platforms) with an
     * addition of `*` as a catch-all case. `ExternalRedirectGuard` is required to be added to route guards for this
     * setting to have an effect. It will go through the list of redirects and match the first one that applies.
     *
     * `desktop` can't always be used in place of `*`. A Windows tablet would match `tablet`, `mobile` and `mobileweb`
     * but not `desktop`.
     *
     * @example
     * {
     *     "/journey-planner/result": [
     *         ["iphone", "https://apps.apple.com/app/id6470642427"],
     *         ["android", "https://play.google.com/store/apps/details?id=se.vl.app"],
     *         ["*", "https://vl.se/biljetter/kopa-biljett/vl-appen/"]
     *     ]
     * }
     **/
    EXTERNAL_REDIRECTS?: Record<string, [PlatformMatch, string][]>;
    /** How many recently departed journeys to show how much time ago*/
    SHOW_LAST_DEPARTED_JOURNEYS: {
        MAX_JOURNEYS: number;
        MAX_SECONDS_IN_PAST: number;
    };
    BENEFICIAL_CHECKBOX: boolean;
    /** Show additional Stop information in Explore */
    SHOW_PICK_UP_DROP_OFF_INFO?: boolean;
    REDIRECT_TO_LOGIN_AFTER_REGISTRATION?: boolean;
    /** Show the heading of the bus icon in Explore when line is selected */
    SHOW_LINE_BUS_HEADING?: boolean;
    /** Show alerts in Explore and JP which match this route direction (otherwise ignore direction)*/
    DIRECTIONAL_ALERTS?: boolean;
    /** Show the date ticket was refunded in tickets history view */
    SHOW_HISTORY_TICKET_REFUND_DATES?: boolean;
    /** Show the date ticket was canceled in tickets history view */
    SHOW_HISTORY_TICKET_CANCELED_DATES?: boolean;
    /** WELL_KNOWN DOMAIN ASSOCIATED WITH THE APPLICATION
     * It could be used to specify the universal link domain for the BE
     * So the BE knows the domain that should be used while redirecting to the application using the universal link
     * */
    ASSOCIATED_DOMAIN?: string;
    ENABLE_FULL_TIME_SELECTION?: boolean;
    /** Show possibility to buy travel_card voucher */
    TRAVEL_CARD_VOUCHER_ENABLED?: boolean;

    /** Default start state from now for history tickets search */
    HISTORY_TICKET_SEARCH_START_DATE_MONTHS_AGO?: number;
    APP_STORE_LINK?: string;
    PLAY_STORE_LINK?: string;

    /** Corporate web show departments in companies view */
    SHOW_DEPARTMENTS?: boolean;
    FEEDBACK_SURVEY_URL?: string;

    SHOW_TRIP_SUMMARY_PRICE_ADDITIONAL?: boolean;
    SHOW_ACTIVATIONS_LEFT_IN_TITLE?: boolean;
    HIDE_SKELETON_LOADER: boolean;
    /** Show to whom the ticket was lent in ticket list view */
    TICKET_LIST_SHOW_LENT_EMAIL?: boolean;
    TICKET_LIST_SHOW_ACTIVATIONS_LEFT?: boolean;
    FORCE_USE_DEVICE_TOKEN?: boolean;
    /** Set to true if we still need to display the legacy notification settings. */
    SHOW_LEGACY_NOTIFICATION_SETTINGS_PAGE?: boolean;
    ENABLE_WHEEL_DATE_TIME_PICKER: boolean;
}

export enum PRODUCT_AREA_DISPLAY_OPTION {
    FULL_MAP = 'FULL_MAP',
    SPLIT_AREA = 'SPLIT_AREA'
}

export interface AmountValidators {
    /** Default: '[0-9]* */
    PATTERN?: string;
    /** Default: 1 */
    MIN_LENGTH?: number;
    /** Default: 4 */
    MAX_LENGTH?: number;
}

export interface MobileMenuCustomization {
    ALTERNATIVE_FOOTER?: boolean;
    ALTERNATIVE_USER_ICON?: boolean;
    ALTERNATIVE_LOGO?: boolean;
    IS_PAGE?: boolean;
    SHOW_ACCESSIBILITY_ITEM?: boolean;
    LOGOUT_TITLE_NOTIFICATION?: boolean;
    SHOW_USER_PHOTO_PLACEHOLDER?: boolean;
    SHOW_LOGOUT_UNDER_APP_LOGO?: boolean;
    SHOW_LOGOUT_TEXT_ARROW?: boolean;
    HIDE_ADDITIONAL_ACCESSIBILITY_BUTTON?: boolean;
    USE_MENU_ICON?: boolean;
    PROFILE_FIELDS_WITH_BORDER?: boolean;
    SHOW_ACTION_BUTTON_ICONS?: boolean;
}

export interface BobSettings {
    AZTEC_UPDATE_SECONDS: number;
    APP_ID: string;
    SET_FULL_BRIGHT?: boolean;
    PARTICIPANT_ID: string;
}

export interface AccountSettings {
    /** Actions to show in my profile submenu */
    ACTIONS?: (
        | 'related-cards'
        | 'cards'
        | 'tickets'
        | 'purchase-history'
        | 'validation-history'
        | 'company-users'
        | 'switch-company'
        | 'seutulippu'
        | 'bookings'
        | 'ride-history'
        | 'company-profile'
        | 'notification-settings'
        | 'account-profile'
    )[];
    /** Form fields to display under account-profile */
    FORMFIELDS?: (
        | 'name'
        | 'email'
        | 'contact_person'
        | 'phone'
        | 'city'
        | 'street'
        | 'postal_code'
        | 'entitlements'
        | 'balance'
    )[];
    /**
     * Actions to show in account details dropdown
     * (values come from CustomerProfileActions, which is impossible to import due co circular dependency)
     */
    PROFILE_ACTIONS?: (
        | 'ACCOUNT_ACTION_EDIT'
        | 'ACCOUNT_ACTION_CHANGE_PASSWORD'
        | 'ACCOUNT_ACTION_CHANGE_EMAIL'
        | 'ACCOUNT_ACTION_DELETE_ACCOUNT'
    )[];
    /** Turn on personalization feature */
    PERSONALIZATION_ENABLED?: boolean;
    /** Turn on bank card personalization */
    BANK_CARD_PERSONALIZATION_ENABLED?: boolean;
    SHOW_TRAVEL_CARD_PERSONALIZATION_STATUS?: boolean;
    /** Show new notification-settings page layout */
    SHOW_NEW_NOTIFICATION_SETTINGS?: boolean;
}

export interface EmptyCartOptionsSettings {
    /** Action button to show on empty cart page */
    SHOPPING_CART_TYPE?: EmptyCartOptions;
}

export interface FooterSettings {
    /** List of footer actions column links (new STW footer in Pilet.ee uses a PO tag for that) */
    ACTIONS?: (
        | 'purchase-ticket'
        | 'purchase-wallet'
        | 'book-a-ride'
        | 'personalize'
        | 'bankcard-tickets'
        | 'purchase-history'
        | 'account-profile'
        | 'related-cards'
        | 'login'
        | 'register'
    )[];
    EXTRA_COLUMN?: boolean;
    HIDE_LOGO?: boolean;
    LOGO_FILE_CONTRAST?: string;
    LOGO_FILE?: string;
    MENU_ITEMS?: MenuItem[];
    /** List of footer social links */
    SOCIAL_LINKS?: ('facebook' | 'twitter' | 'instagram' | 'linkedin')[];
    /** List of footer support column links */
    SUPPORT_ACTIONS?: ('mail' | 'phone' | 'feedback' | 'FAQ' | 'extra_row' | 'report' | 'switchCorp')[];
    WEB_PURCHASE_LINKS?: boolean;
    ENABLE_FIVE_COLS_FOOTER?: boolean;
    EXTERNAL_LINKS?: {
        explore?: string;
    };
}

export interface FormattingSettings {
    /**
     * Controls where transportation day offset must be subtracted from before formatting.
     *
     * Applies only if you also specify formatting options for the provided keys (could be `{}` for browser defaults)
     */

    /** Mostly for Swedish clients who want to keep the date-time format consistent across languages (e.g., yyyy-MM-dd HH:mm for both English and Swedish). */
    USE_SETTINGS_DATE_FORMAT_FOR_ALL_LANGUAGE?: boolean;

    /**
     * By default, we set the start time to T00:00:00.
     * If this setting is enabled, no change is applied.
     *
     * Example:
     *   - Default behavior: "2025-11-24T15:17:12" -> "2025-11-24T00:00:00"
     *   - When enabled:     "2025-11-24T15:17:12" -> "2025-11-24T15:17:12"
     */
    KEEP_ORIGINAL_STARTING_TIME_FOR_PERIOD_TICKET?: boolean;
    /**
     * Now, we set the end time to 23:59.
     * If this setting is enabled, no change is applied.
     *
     * Example:
     *   - Default behavior: "2026-01-25T03:59:59" -> "2026-01-25T23:59:00"
     *   - When enabled:     "2026-01-25T03:59:59" -> "2026-01-25 03:59"
     */
    KEEP_ORIGINAL_ENDING_TIME_FOR_PERIOD_TICKET?: boolean;
    /**
     * When true, apply transport day offset to ticket ending times. Primary source.
     */
    USE_TRANSPORTATION_DAY_OFFSET?: boolean;
    /**
     * Product types that skip transportation day offset. These show exact ending time.
     * Default: ['M', 'H'] (minute-based and hour-based tickets).
     */
    SHORT_PERIOD_PRODUCT_TYPES_SKIP_OFFSET?: string[];
    /**
     * Opt-in: when `true`, ticket validity UIs that support it (e.g. `ridango-period-ticket`) show starting and ending,
     * not end-only. Omitted or `false` leaves prior behaviour (end-only when the parent passes `showValidityStart` false, etc.).
     */
    SHOW_TICKET_AS_VALIDITY_RANGE?: boolean;
    /**
     * VL-style unified validity: weekday + yyyy-MM-dd HH:mm, spaced hyphen separator, capitalized weekday.
     * Used with USE_SETTINGS_DATE_FORMAT_FOR_ALL_LANGUAGE and TICKET_VALIDITY_RANGE_SEPARATOR.
     */
    UNIFIED_TICKET_VALIDITY_DISPLAY?: boolean;
    /** Range separator between start and end validity segments. Default ` - `. */
    TICKET_VALIDITY_RANGE_SEPARATOR?: string;
    /**
     * @deprecated Use USE_TRANSPORTATION_DAY_OFFSET (top-level or PURCHASE) + SHORT_PERIOD_PRODUCT_TYPES_SKIP_OFFSET instead.
     * Per-context toggles are no longer needed; offset applies uniformly when enabled.
     */
    SUBTRACT_TRANSPORTATION_DAY_OFFSET_FROM?: (
        | 'MY_TICKET_VALIDITY'
        | 'PERIOD_TICKET_VALIDITY'
        | 'TICKET_SUMMARY_VALIDITY'
        | 'CARD_TICKETS_LIST'
    )[];
    /** Controls formatting for `ridango-hours-ticket` component */
    HOURS_TICKET_VALIDITY?: DateTimeFormatOptions;
    /** Controls formatting for `ridango-my-ticket` (movingo) component */
    MY_TICKET_VALIDITY?: DateTimeFormatOptions;
    /** Controls formatting for `ridango-my-ticket` (movingo) component */
    MY_TICKET_SUB_TICKET_VALIDITY?: DateTimeFormatOptions;
    /** Controls formatting for `ridango-period-ticket` component */
    PERIOD_TICKET_VALIDITY?: DateTimeFormatOptions;
    /** Controls formatting for `ridango-ticket-summary` (movingo) and `ridango-ticket-summary-v2` (klaipeda, sormland) components */
    TICKET_SUMMARY_VALIDITY?: DateTimeFormatOptions;
    /** Controls formatting for `rwidget-disruption-messages-accordion` */
    DISRUPTION_MESSAGE_DATE_RANGE?: DateTimeFormatOptions;
}

export interface DateTimeFormatOptions extends Intl.DateTimeFormatOptions {
    /** Specifies locale for weird clients who requested to ignore user's preferred locale */
    locale?: string;
    /* eslint-disable no-irregular-whitespace */
    /**
     * Specifies range separator. To be used in case the client doesn't like that DateTimeFormat.formatRange collapses.
     * Opts-out of DateTimeFormat.formatRange usage, forces `startDate + rangeSeparator + endDate` instead.
     *
     * @example
     * // "sv" locale, { dateStyle: "short" }
     * "2024-05-15 – 06-13" // no rangeSeparator
     * "2024-05-15 – 2024-06-13" // { rangeSeparator: " – " }
     **/
    /* eslint-enable no-irregular-whitespace */
    rangeSeparator?: string;
}

export interface HeaderSettings {
    HIDE_CONTACT_EMAIL?: boolean;
    HIDE_CONTACT_PHONE?: boolean;
    REDIRECT_TO_MY_ACCOUNT_WEB?: string;
    /** Hides language selector */
    HIDE_LANGUAGE_SELECTOR?: boolean;
    /** Hides login, used for SR SSO page */
    HIDE_LOGIN_STATUS?: boolean;
    HIDE_REGISTRATION_BUTTON?: boolean;
    /** Hides mobile menu, used for SR SSO page */
    HIDE_MOBILE_MENU?: boolean;
    HIDE_HORIZONTAL_MENU?: boolean;
    /** Hides navigation bar, used for SR SSO page */
    HIDE_NAV?: boolean;
    /** Hides shopping cart, used for SR SSO page */
    HIDE_SHOPPING_CART?: boolean;
    HIDE_FEEDBACK?: boolean;
    SHOW_MESSAGES?: boolean;
    SHOW_FAQ?: boolean;
    SHOW_EXTERNAL_SUPPORT_URL?: boolean;
    HIDE_PAYMENT_SETTINGS?: boolean;
    HIDE_BIKE_GARAGE?: boolean;
    MENU_ITEMS_ORDER?: HeaderElement[];
    MOBILE_MENU_ITEMS_ORDER?: HeaderElement[];
    DELAY_COMPENSATION_LINK?: Record<string, string>;
    /** List of menu links */
    LINKS?: (
        | 'buy-ticket'
        | 'add-money'
        | 'personalize'
        | 'bike-garage'
        | 'map'
        | 'book-a-ride'
        | 'ondemand-trips'
        | 'my-account'
        | 'bankcard-tickets'
    )[];
    EXTERNAL_LINKS?: ExternalLinksConfig;
    LOGO_FILE_CONTRAST?: string;
    LOGO_FILE?: string;
    /** Display customer email instead of name in header */
    SHOW_EMAIL_INSTEAD_NAME?: boolean;
    /** Dropdown menu system */
    DROPDOWN_MENU?: {
        ENABLED?: boolean;
        ITEMS?: DropdownMenuItem[];
        DATA?: { [key: string]: DropdownMenuData };
    };
    HAMBURGER_MENU_TO_RIGHT?: boolean;
    DISPLAY_BIGGER_LOGO?: boolean;
    LOGO_TO_EXTERNAL_LINK?: boolean;
}

export interface HomeSettings {
    SECTIONS?: ('LANDER' | 'ACTIONS' | 'CAROUSEL' | 'BENEFITS' | 'NOTICES')[];
    SHOW_TRAVEL_CARD_LOGO?: boolean;
}

export interface AppNotifications {
    ENABLED?: boolean;
    /** Use new state based notifications */
    USE_STATE: boolean;
    /** Hides the notification slide-toggle button */
    HIDE_IN_APP_TOGGLE: boolean;
    /** Show link to take user into OS Notification Settings */
    SHOW_REDIRECT_TO_OS_SETTINGS: boolean;
}

export enum NotificationPreferenceSetting {
    RECEIVE_RECEIPT = 'RECEIVE_RECEIPT',
    GENERIC_MESSAGE = 'GENERIC_MESSAGE',
    TICKET_EXPIRATION = 'TICKET_EXPIRATION'
}

export type NotificationPreferenceConfig = Partial<
    Record<NotificationPreferenceChannel, NotificationPreferenceSetting[]>
>;

export interface LastDepartedJourneySettings {
    /** How many recently departed journeys to show from now */
    MAX_JOURNEYS: number;
    /** How much time to allow for a journey to be considered recently departed from now */
    MAX_SECONDS_IN_PAST: number;
    /** How many journeys to show before arrival time initially */
    JOURNEYS_BEFORE_ARRIVE_BY: number;
    /** How many journeys to show after arrival time initially*/
    JOURNEYS_AFTER_ARRIVE_BY: number;
    /** How many additional journeys to show after arrival time when loading more */
    JOURNEYS_BEFORE_ARRIVE_BY_MORE: number;
    /** How many additional journeys to show before arrival time when loading more */
    JOURNEYS_AFTER_ARRIVE_BY_MORE: number;
    /** When given includes additional journey closest to datetime by departure (adds +1 journey) */
    INCLUDE_OPTIMAL_JOURNEY: boolean;
    /** When given hides the optimal journey icon */
    OPTIMAL_JOURNEY_ICON_VISIBLE?: boolean;
}

export interface JourneyPlannerSettings {
    SHOW_LAST_DEPARTED_JOURNEYS: LastDepartedJourneySettings;
    /** Hides "Leaves in 3 minutes..." message */
    HIDE_LEAVES_IN?: boolean;
    HIDE_LEAVE_NOW?: boolean;
    SHOW_DEPARTED_DELAY_SECONDS?: number;
    PROVIDER?: 'ETIS' | 'OTP';
    REALTIME_AVAILABLE?: boolean;
    REALTIME_ICON_HIDDEN?: boolean;
    SHOW_MUNICIPALITY_IN_STOP_NAME?: boolean;
    PLANS_MAX_NUMBER_OF_ITINERARIES?: number;
    PLANS_SEARCH_WINDOW_HOURS?: number;
    SHOW_FULL_MAP?: boolean;
    UPDATE_INTERVAL_SECONDS?: number;
    SHOW_GROUPED_STOP_ROUTES: boolean;
    HIDE_FAVORITE_FROM_MAP?: boolean;
    SHOW_FAVORITES?: boolean;
    SHOW_CANCELLED_STATUS?: boolean;
    SHOW_ALERT_BUTTON_MORE?: boolean;
    SHOW_SEARCH_HISTORY?: boolean;
    NOT_CLEAR_COORDINATES?: boolean;
    SHOW_FULL_WIDTH_BUTTON?: boolean;
    SHOW_PRICES?: boolean;
    HIDE_PRICE_BLOCK_IF_PRICE_UNAVAILABLE?: boolean;
    SHOW_LEG_PRICES?: boolean;
    SHOW_PLATFORM_PREFIX?: boolean;
    SHOW_SELECTED_ITINERARY_BUSSES?: boolean;
    SHOW_FARE_URL_ITINERARY_INSTRUCTIONS?: boolean;
    MINIMUM_VISIBLE_DISTANCE?: number;
    SHOW_SCHEDULE?: boolean;
    SHOW_FAVORITES_STOPS_AND_LOCATIONS?: boolean;
    SHOW_STOP_POPUP_ON_MAP?: boolean;
    SHOW_BUSES?: boolean;
    SEARCH_BY_COORDINATES?: boolean;
    /** Prefer to search journey by stop id if possible*/
    SEARCH_BY_STOP_ID?: boolean;
    FILTERS?: JourneyPlannerFiltersSettings;
    IMAGE_MAP_LOCATION_PATH?: string;
    /** This setting is to configure showing disruption messages on Journey planner in general */
    SHOW_DISRUPTION_MESSAGES?: boolean;
    /** This setting is to configure showing of "We may have problems with this specific journey plan" message on trip details */
    SHOW_GENERAL_DISRUPTION_MESSAGE?: boolean;
    IS_PUBTRANS_MESSAGE: boolean;
    BUTTONS: string[];
    SHOW_ROUTE_SHARE?: boolean;
    /** This setting is to draw empty leg point circle */
    SHOW_EMPTY_LEG_POINT_CIRCLE?: boolean;
    GENERAL_SELECTED_BUS_TRIP_ICON?: boolean;
    SCHEDULE_TIME_THRESHOLD_EARLY?: number;
    SCHEDULE_TIME_THRESHOLD_LATE?: number;
    /** When arriveBy is set to true shows shows journeys with endTime closest to arrival Time */
    SORT_BY_CLOSEST_ARRIVAL?: boolean;
    /** A multiplier for how bad walking is, compared to being in transit for equal lengths of time. */
    WALK_RELUCTANCE?: number;
    /** A multiplier for how bad waiting is, compared to being in transit for equal lengths of time. */
    WAIT_RELUCTANCE?: number;
    /** Clears search parameters when navigating away from view, prevents search happening when user returns */
    CLEAR_JP_AFTER_LEAVE?: boolean;
    SHOW_TICKET_PURCHASE_TIME_MODAL?: boolean;
    MAX_MINUTES_UNTIL_DEPARTURE?: number;
    MIN_MINUTES_BEFORE_DEPARTURE?: number;
    EXPAND_SEARCH_FOR_LIMITED_TRIPS?: boolean;
    LIMITED_TRIPS_THRESHOLD?: number;
    /** Setting this to true means that stops toggle won't be hidden in JP details view  */
    ALWAYS_SHOW_STOPS_TOGGLE?: boolean;
    USE_TRASH_BIN_FOR_REMOVE_FAVORITE_STOP?: boolean;
    SHOW_ICON_FOR_ITINERARY_INSTRUCTIONS_STOP_TOGGLE?: boolean;
    SHOW_ICON_FOR_SHOW_MORE?: boolean;
}

export interface MapSettings {
    ZOOM_CONTROLS_HIDDEN: boolean;
    INITIAL_ZOOM_MAP?: number;
    /** Map default location coordinates */
    COORDINATES?: {
        latitude: number;
        longitude: number;
    };
    /** Hides journey planner functionality  */
    HIDE_JOURNEY_PLANNER?: boolean;
    /** Enables and sets zoom level for stop icons clustering  */
    CLUSTER_STOP_ICONS_ZOOM?: number;
    MAX_BOUNDS?: [[number, number], [number, number]];
    MAX_ZOOM?: number;
    /**
     * Defines the minimum zoom size at which bus stop icons become visible on the Leaflet map.
     *
     * Uses the existing IconSizeEnum values (`'s' | 'm' | 'l'`), which are already applied
     * across the application to control visibility of other map elements (e.g., bus icons, popups).
     *
     * The map icon util maps the current zoom level to one of these size values,
     * and bus stops will be shown only when the current size is equal to or greater
     * than the specified value.
     */
    STOPS_VISIBLE_FROM_ZOOM_SIZE?: IconSizeEnum;
}

export interface OnboardingFlowSettings {
    SLIDES?: number;
    TYPE?: 'ICON' | 'IMAGE';
}

export interface OnboardingLoginSettings {
    REQUIRE_LOGIN?: boolean;
    USE_BACKGROUND_IMAGE?: boolean;
    USE_OPTIONAL_TEXT?: boolean;
    /** Used for the redirection URL from login providers */
    LOGIN_RETURN_URL?: string;
    SHOW_DIVIDER_BETWEEN_REGISTER_AND_SKIP?: boolean;
    SHOW_ARROW_AFTER_REGISTER_AND_SKIP?: boolean;
}

export interface OnboardingSettings {
    ENABLE_ONBOARDING_FLOW?: boolean;
    DISABLE_CLICKABLE_PAGINATION?: boolean;
    SHOW_BACK_BUTTON?: boolean;
    SHOW_TITLE_STEPS?: boolean;
    BACK_BUTTON_LANGUAGE?: boolean;
    BACK_BUTTON_LOGIN_AFTER_ONBOARDING?: boolean;
    SUB_PAGES?: ('LANGUAGE_SELECTION' | 'LOGIN' | 'ONBOARDING_FLOW' | 'TERMS')[];
    FLOW?: OnboardingFlowSettings;
    LOGIN?: OnboardingLoginSettings;
    FORGOT_PASSWORD_NEW?: boolean;
    RECOVER_PASSWORD_NEW?: boolean;
    LANGUAGE_SELECTION_ORDER?: Array<string>;
    TNC_SPECIFIC_IMG?: boolean;
}

export interface OndemandSettings {
    CARD_TYPE_MASK?: number;
    PAYMENT_ON_WEB?: boolean;
    RIDE_HISTORY_MONTHS_IN_FUTURE?: number;
    SHOW_LINKS?: boolean;
    DISABLE_ALLOW_POINT_CLICK?: boolean;
}

export interface PurchaseSettings {
    /** Show whole county in zones default true */
    WHOLE_COUNTY_OPTION?: boolean;
    AUTOSELECT_PRODUCT_CATEGORY?: boolean;
    /** Used for determining if the user can have access to any pages from the purchase flow without logging in (e.g. purchase/cart, purchase/ticket) */
    DISABLE_UNAUTHORIZED_PURCHASE?: boolean;
    /** Enables showing extra information in the dialog of the map view, under the common description on top of the map image */
    EXTRA_ZONE_MAP_INFO?: boolean;
    /** List and order of sections to show on purchase flow */
    SECTIONS?: (
        | 'ZONE'
        | 'TRAVELLER_CATEGORY'
        | 'PRODUCT_CATEGORY'
        | 'AREA_CATEGORY'
        | 'PRODUCT'
        | 'GROUP'
        | 'PRODUCT_QUANTITY'
        | 'VALIDITY'
        | 'MEDIUM'
    )[];
    /** Whether to show new medium selection component with radio buttons (multiple mediums) or old medium selection component (email or travel card) */
    SHOW_MEDIUM_SELECTION_RADIO?: boolean;
    /** Whether to merge travelcard medium types to display only one. LAP-289 */
    MERGE_MEDIUM_TRAVELCARDS?: boolean;
    TRAVEL_BETWEEN_ZONES?: boolean;
    /** Temporary flag to turn on transportation day offset (will be always on in the future) */
    USE_TRANSPORTATION_DAY_OFFSET?: boolean;
    /** If true, skips transportation day offset for short-duration tickets (e.g. single 3h tickets) */
    SKIP_OFFSET_FOR_DURATION_BASED_TICKETS?: boolean;
    /** Gets payment methods by channel only instead of depending on the current cart */
    GENERIC_PAYMENT_METHODS?: boolean;
    ALTERNATIVE_PRODUCT_DESCRIPTION_TRANSLATION: { [key: string]: { [key: string]: string } };
    MAX_TICKETS_ALLOWED?: number;
    HIDDEN_EMV_SCHEMES?: string[];
}

export interface ProfileUpdateOptions {
    FIRST_NAME_MANDATORY?: boolean;
    LAST_NAME_MANDATORY?: boolean;
    PHONE_NUMBER_MANDATORY?: boolean;
}

export interface RegistrationOptions {
    /** Enables showing extra checkbox for user to agree with GDPR on registration */
    CHECK_GDPR?: boolean;
    /** Display full name during registration */
    DISPLAY_FULL_NAME_FIELD?: boolean;
    /** Automatically login user when his account is verified. True by default (if not set) */
    LOGIN_WHEN_VERIFIED?: boolean;
    HIDE_NAME_FIELD?: boolean;
    FULL_NAME_IS_OPTIONAL?: boolean;
    DIVIDED_FIRST_AND_LAST_NAME?: boolean;
    LOGIN_WHEN_RESET_PASSWORD?: boolean;
    PERSONAL_DATA?: boolean;
    PERSONAL_DATA_LINK?: string;
    AUTH_FEATURES?: string[];
    CREATE_ACCOUNT_INFORMATION?: boolean;
    // TODO: the registration page code should be refactored to be built only by this setting
    /** Should be usedd be used to make to configure a list of fields at the registration page*/
    FIELD_ORDER?: RegistrationFieldOrderConfig;
    OIDC_SIGNUP_COMPLETE_REQUIRED?: boolean;
    OIDC_SIGNUP_COMPLETE_FIELD_ORDER?: RegistrationFieldOrderConfig;
    /** Disable register button if terms&conditions are not checked(true) */
    DISABLE_REGISTER_BUTTON_TERMS_NOT_CHECKED?: boolean;
    /** Enable register button initially */
    ENABLE_REGISTER_BUTTON?: boolean;
    /** Disable focus jump on form invalid submit */
    DISABLE_FOCUS_ON_FORM_INVALID?: boolean;
    /** Enable registration progress steps display */
    ENABLE_REGISTRATION_STEPS?: boolean;
    /** Shows forward arrow after tnc and gdpr links */
    SHOW_ARROW_AFTER_LINK?: boolean;
    USE_COUNTRY_CODE_ON_FORMS?: boolean;
}

export interface RegistrationFieldOrderConfig {
    [key: string]: {
        OPTIONAL: boolean;
        VALIDATION?: {
            REGEXP?: string;
            TRANSLATION_LABELS: {
                BLANK?: string;
                MIN_LENGTH?: string;
                CUSTOM?: string;
            };
        };
    };
}

export interface RelatedCardSettings {
    /** Edit/remove/add pin/change pin - not set in Ansible, done in appconfig */
    ACTIONS?: CardActionsFeature[];
    /** Hides blocking option from a user */
    DISALLOW_BLOCK?: boolean;
    /** Enables showing an unblocking option to a user */
    ALLOW_UNBLOCK?: boolean;
    /** If set to `true`, user is asked for replacement card address details on blocking */
    BLOCK_WITH_FORM?: boolean;
    /** Allows to check if related card is not registered or is registered for another account */
    CHECK_STATUS?: boolean;
    /** Disables pin field for related card adding */
    HIDE_PIN?: boolean;
    /** Enables showing card type selection in related card adding component */
    SHOW_CARD_TYPES?: boolean;
    /** Enables showing discount selection in related card adding component */
    SHOW_DISCOUNTS?: boolean;
    /** Enables showing discount groups for cards detail view */
    SHOW_DISCOUNT_GROUPS?: boolean;
    /** Hides 'Buy Ticket' button from card contextual menu */
    HIDE_BUY_TICKET?: boolean;
}

export interface StopOptions {
    NOT_EXPANDABLE_FILTER: boolean;
    SHOW_DEPART_ARRIVE_TOGGLE: boolean;
    DEPART_FILTER?: {
        /** Route icon size for route filters in stop details departure filter */
        ROUTE_ICON_SIZE?: 'md' | 'lr' | 'xl' | `${number}rem`;
        /** Route icon text size for route filters in stop details departure filter */
        ROUTE_ICON_TEXT_SIZE?: 'xs' | 'sm' | 'md' | 'lg';
        /** Disabled route icon text and border color for stop details departure filter */
        ROUTE_ICON_DISABLED_COLOR?: string;
    };
}

export interface DisruptionMessagesSettings {
    COLLAPSE_SYSTEM_STATUS?: boolean;
    COLLAPSE_FILTER_ALERTS?: boolean;
    HIDE_BACK_BUTTON?: boolean;
    ENABLE_FILTERS?: boolean;
    HIDE_SYSTEM_STATUS?: boolean;
    SHOW_ROUTES_WITH_STOPS?: boolean;
    FILTERS?: DisruptionFilters[];
    MESSAGES_TYPES?: string[];
    CUSTOM_ICONS?: DisruptionMessageType[];
    /** Show all alerts when opening disruptions view by default */
    SHOW_ALL_ALERTS_ON_OPEN: boolean;
}

export interface WalletV2Settings {
    STEPS: WalletV2Step[];
}

export interface WalletV2Step {
    name: string;
    title: string;
}
export interface PurchaseFlowSettings {
    /** Show component to choose between personal and corporate accounts  */
    SHOW_USER_ACCOUNT_CHOICE?: boolean;
    REDIRECT_TO_START_ON_REFRESH?: boolean;
    SHOW_ANONYMOUS_USER_MODAL_ALWAYS: boolean;
    HIDE_SHOPPING_CART_BACK_BUTTON: boolean;
    HIDE_WALLET_LOADING_BACK_BUTTON: boolean;
    UTN_TRAVEL_CARD_LENGTH: number;
    MATKAKORTTI_LENGTH: number;
    ISIC_CARD_LENGTH: number;
    TICKET_STEPS: string[];
    SORTING_BY_CODE?: boolean;
    BOOKLET_JP_ENABLE?: boolean;
    TICKET_STEPS_DESCRIPTION?: string[];
    SECOND_PRODUCT_AREA_TAB?: boolean;
    SHOW_RECENT_PURCHASE?: boolean;
    HIDE_MAP?: boolean;
    USE_SMALL_BUTTONS?: boolean;
    HIDE_CLOSE_BUTTON?: boolean;
    PAYMENT_TYPE_COMPANY_ENABLED?: boolean;
    SHOW_OTHER_PAYMENT_METHODS?: boolean;
    HIDE_PRODUCT_CATEGORY_DESCRIPTION?: boolean;
    ADDITIONAL_DESCRIPTION_FROM_A_TO_B?: boolean;
    SAVE_PREFERENCE_ENABLED?: boolean;
    PREFERENCES_OLD_ADD_TRAVEL_CARD_LINK?: boolean;
    MULTI_TICKET_WARNING?: boolean;
    PREFERENCES_LABEL_ENABLED?: boolean;
    VALUE_CODE_ENABLED?: boolean;
    SHOW_VALUE_CODE_BUTTON_ICON?: boolean;
    ENABLE_DETAILED_PAYMENT_METHODS?: boolean;
    FREE_TRAVEL_MESSAGE_IN_DESCRIPTION?: boolean;
    TICKET_CONF?: TicketConfSettings;
    SHOW_TICKET_DURATION_DESCRIPTION: boolean;
    ACTIVATION_CHECK_VALIDITY: boolean;
    HIDE_INLINE_MANUAL_ACTIVATION_BUTTON?: boolean;
    SHOW_ZONE_MAP_IN_AREA_STEP?: boolean;
    SHOW_TICKET_ICON_IN_SHOPPING_CART?: boolean;
    MINIMUM_OTHER_PURPOSE_LENGTH: number;
    USE_STATIC_TICKET_TITLE?: boolean;
    SHOW_SET_OTHER_TIME_BUTTON_ICON?: boolean;
    USE_STATIC_TITLE_MULTI_JOURNEY?: boolean;
    /** Show zone count text in journey planner purchase category-product step */
    SHOW_ZONE_IN_JOURNEY_PLANNER_PURCHASE?: boolean;
    /** Show area grid even for COUNTY product area type (Örebro, Värmland specific) */
    SHOW_COUNTY_AREA_GRID?: boolean;
}

export interface TicketConfSettings {
    PREFERENCES?: PreferencesSettings;
    SHOPPING_CART?: ShoppingCartSettings;
}

export interface PreferencesSettings {
    MEDIA_OPTIONS?: TravelMedia[];
    PERSONAL_CODE_REGEXP?: string;
    COMBINED_PERSONAL_CODE_TRAVEL_CARD?: boolean;
    BANK_CARD_ENABLE?: boolean;
}

export interface ShoppingCartSettings {
    TERMS_CHECKBOX_VISIBILITY?: TermsCheckboxVisibility;
    ZONES_IN_TICKET_NAME?: boolean;
    GROUP_COUNT_BELOW?: boolean;
    /** Time before cart end to show the warning dialog (minutes) */
    WARNING_DIALOG_TIMER?: string;
    /** Show value codes under individual tickets instead of at cart level */
    SHOW_VALUE_CODES_PER_TICKET?: boolean;
}

export enum TermsCheckboxVisibility {
    EVERYONE = 'EVERYONE',
    ANONYMOUS = 'ANONYMOUS',
    HIDDEN = 'HIDDEN'
}

export interface JourneyPlannerFiltersSettings {
    TRANSPORT_MODE: boolean;
    /** If true, transport mode labels will be capitalized (first letter uppercase) in filter modal */
    CAPITALIZE_TRANSPORT_MODE_LABELS?: boolean;
    ROUTE: boolean;
    FILTER_ROUTES_LIMIT: number;
    SHOW_FILTERS_IN_MODAL: boolean;
    SELECTORS: Array<JourneyPlannerFilterItemSettings>;
}

export interface JourneyPlannerFilterItemSettings {
    TITLE_KEY: string;
    QUERY_PARAMETER: string;
    INIT_VALUE: string | number;
    TYPE: 'MULTI_SELECTION' | 'SINGLE_SELECTION' | 'SLIDER' | 'VIA_POINT';
    VALUES: Array<JourneyPlannerFilterItemValueSettings>;
}

export interface JourneyPlannerFilterItemValueSettings {
    KEY?: string;
    SELECTED_BY_DEFAULT?: boolean;
    VALUE?: string | number;
}

export enum BusIconTypeEnum {
    DYNAMIC = 'DYNAMIC'
}

export interface OfflineModeSettings {
    MENU_ITEMS?: string[];
    CACHE_TICKETS?: boolean;
    CACHE_FAVORITE_SCHEDULES?: boolean;
    DEFAULT_NAVIGATION?: string[];
    // time after which offline tickets array will be reset (in minutes)
    OFFLINE_TICKETS_DISPLAYED_TIME?: number;
}

enum IconSizeEnum {
    small = 's',
    medium = 'm',
    large = 'l'
}

type PlatformMatch = keyof PlatformConfig | '*';
